/**
 * The site's artwork and its white counterparts.
 *
 * A drawing does not follow the palette the way type and rules do: it is a
 * picture, set once in the identity's anthracite, and against a dark background
 * it thins out. A CSS filter is not the fix - inverting the anthracite would
 * take the CI blue with it - so every drawing that has to survive the dark view
 * ships twice, the second export named `_weiss`.
 *
 * The pairs live here rather than in the components that place them, because
 * one of the pictures is chosen by an editor: `titleImage` at /admin is a path
 * typed into a field, and the page header has no other way of knowing that a
 * white export of it exists. Everything else looks its pair up here for the
 * same reason a component does not hold two file names of its own - the list of
 * what has a white export is one list, and it is this one.
 *
 * A path that is not in the list is returned unchanged: artwork an editor
 * uploads has one export and stands in both views.
 */

/** Which view a drawing is being placed in; MUI's palette mode by another name. */
export type ArtworkMode = 'light' | 'dark';

/**
 * The white export of each drawing, keyed by the anthracite one as it is
 * written in the content and in the components - an absolute path under
 * `public/`, which is what reaches the `src` attribute.
 */
export const WHITE_EXPORTS: Readonly<Record<string, string>> = {
  '/Logo.png': '/Logo_weiss.png',
  '/Logo_ohne_Schrift.png': '/Logo_ohne_Schrift_weiss.png',
  '/Kollektiv_Bibliothek.png': '/Kollektiv_Bibliothek_weiss.png',
  '/Ziele.png': '/Ziele_weiss.png',
};

/**
 * The file to place for a drawing in the view being rendered.
 *
 * `/Logo_Schrift.png` is deliberately absent from the list above: the full logo
 * with its subtitle has no white export, and Logo.tsx falls back to the
 * wordmark rather than to a grey drawing on a dark ground.
 */
export const artworkFor = (src: string, mode: ArtworkMode): string =>
  (mode === 'dark' ? WHITE_EXPORTS[src] : undefined) ?? src;
